import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FolderPlus, Loader2, Plus, Trash2 } from 'lucide-react';
import { useNoticeFiles } from '../useNoticeFiles';
import { useLanguage } from '../../../contexts/LanguageContext';
import { createFolder, type NoticeFileMeta } from '../noticeStore';
import { CompletingInput } from '../CompletingInput';
import { falError } from '../threeDApi';

// The day, and no clock time. A manual is a thing you added, not an event you attended:
// the hour it landed answers nothing anyone reading this list is asking. The month in full, in
// the page's language: "17 July 2026", "17 juillet 2026".
function formatDate(ms: number, language: string): string {
  return new Date(ms).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// The document's display title: "Brand - Category - Modèle", each part dropped
// when the manual doesn't carry it (e.g. no brand yet -> "Category - Modèle", the
// label already being the category -> just the name). Falls back to the PDF
// filename for a manual that hasn't been classified yet (no item name).
function docTitle(f: NoticeFileMeta): string {
  const brand = f.brand?.trim();
  const category = f.category?.trim();
  const name = f.itemName?.trim();
  const body = category && name && category !== name ? `${category} - ${name}` : name;
  if (!body) return f.name; // unclassified: the filename is all we have
  return brand ? `${brand} - ${body}` : body;
}

// The Notice viewer: every manual there is, and the way into each one.
//
// It was two tabs — a reader that reopened whatever you last had open, and a History
// list under a name of its own. Neither could reach the other: the reader had no route
// to the list, and the list did not know which manual the reader was holding. One tab,
// and it opens on the list, because choosing which manual comes before reading it.
// Opening one is /view/<slug>, which is under this address, so the tab stays lit.
//
// Ordered by brand, then item name, then newest first — both derived from the run
// answers, server-side. A manual nobody has classified yet has neither and falls last,
// under its filename.
export function NoticeLibrary() {
  const { files, remove } = useNoticeFiles();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [naming, setNaming] = useState(false);
  const [brand, setBrand] = useState('');
  const [product, setProduct] = useState('');
  const [making, setMaking] = useState(false);
  const [folderError, setFolderError] = useState('');
  const makeFolder = async () => {
    setMaking(true);
    setFolderError('');
    try {
      navigate(`/view/${await createFolder(brand.trim(), product.trim())}`);
    } catch (e) {
      setFolderError(falError(e, t('notice.lib.folderFailed')));
      setMaking(false);
    }
  };

  // Every manual in one flat list — the brand no longer heads a section, it opens
  // each title below (see docTitle). Ordered by brand (unbranded last), then item
  // name, then newest first.
  const sorted = useMemo(() => {
    if (!files) return [];
    // An empty brand sorts after any real one, so unbranded manuals fall last.
    const brandKey = (f: NoticeFileMeta) => f.brand?.trim().toLowerCase() || '￿';
    return [...files].sort(
      (x, y) =>
        brandKey(x).localeCompare(brandKey(y)) ||
        (x.itemName || '').localeCompare(y.itemName || '') ||
        y.addedAt - x.addedAt,
    );
  }, [files]);

  // What the folder's two fields offer to finish (see CompletingInput).
  const known = useMemo(() => {
    const unique = (xs: (string | null | undefined)[]) =>
      [...new Set(xs.map((x) => x?.trim() || '').filter(Boolean))].sort((x, y) => x.localeCompare(y));
    const own = (files ?? []).filter((f) => !brand.trim() || f.brand?.trim().toLowerCase() === brand.trim().toLowerCase());
    return { brands: unique((files ?? []).map((f) => f.brand)), products: unique((own.length ? own : files ?? []).map((f) => f.itemName)) };
  }, [files, brand]);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <div className="flex items-center justify-center">
        <h1 className="font-sora text-2xl font-bold text-white">{t('notice.nav.viewer')}</h1>
      </div>

      {/* The only thing on this page that is not a manual: the one that is not here
          yet. Above the list rather than under it, because a list of twelve puts its
          own end off the screen. */}
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link
          to="/view/new"
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500"
        >
          <Plus className="h-4 w-4" />
          {t('notice.lib.new')}
        </Link>
        {/* OR A FOLDER WITH NO MANUAL, for a product somebody has a 3D model of (see createFolder). */}
        <button
          type="button"
          onClick={() => setNaming((on) => !on)}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-blue-500 hover:text-blue-600"
        >
          <FolderPlus className="h-4 w-4" />
          {t('notice.lib.newFolder')}
        </button>
      </div>
      {naming && (
        <form
          onSubmit={(e) => { e.preventDefault(); void makeFolder(); }}
          className="mx-auto mt-4 flex max-w-md flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
        >
          {/* EACH FINISHED BY TAB from what the library already holds (see CompletingInput): the brands,
              and the products of the brand typed, or of any where none is. */}
          <CompletingInput
            value={brand}
            onChange={setBrand}
            options={known.brands}
            placeholder={t('notice.lib.folderBrand')}
          />
          <CompletingInput
            value={product}
            onChange={setProduct}
            options={known.products}
            placeholder={t('notice.lib.folderName')}
            autoFocus
          />
          {folderError && <p className="text-center text-sm text-rose-600">{folderError}</p>}
          <button
            type="submit"
            disabled={making || !product.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-50"
          >
            {making && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('notice.lib.folderCreate')}
          </button>
        </form>
      )}

      <div className="my-6 border-t border-slate-200" />

      {files === null ? (
        <p className="text-center text-sm text-slate-500">{t('common.loading')}</p>
      ) : files.length === 0 ? (
        <p className="text-center text-sm text-slate-500">{t('notice.lib.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {sorted.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-colors hover:border-blue-500"
            >
              {/* THE BIN'S WIDTH, EMPTY, on the other side — so the text is centred on the card and
                  not on what is left of it beside the bin. */}
              <span aria-hidden className="w-8 shrink-0" />
              <Link
                to={`/view/${f.slug || f.id}`}
                className="flex min-w-0 flex-1 items-center gap-3"
              >
                {/* Title centered in the row, the day it was added under it. The PDF's file name
                    and its size were here too: neither is something a reader picks a manual by. */}
                <div className="min-w-0 flex-1 text-center">
                  <p className="truncate text-sm font-medium text-slate-900">
                    {docTitle(f)}
                  </p>
                  <p className="truncate text-xs text-slate-500">
                    {formatDate(f.addedAt, language)}
                  </p>
                </div>
              </Link>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t('notice.lib.removeConfirm').replace('{name}', f.name)))
                    void remove(f.id);
                }}
                aria-label={`${t('notice.lib.remove')} ${f.name}`}
                className="shrink-0 rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-8 border-t border-slate-200" />
    </div>
  );
}
